import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { saveCreditTopupCheckoutAttribution } from './creditTopupAnalytics';
import { QueryKey } from '@/Config/api/queryKey';
import {
  CreditTopupCheckoutInput,
  CreditTopupCheckoutUrl,
  CreditTopupOffer,
} from '@/Domain/api/Subscription/dto/CreditTopup.dto';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';

const checkoutEventProperties = (
  offer: CreditTopupOffer | undefined,
  input: CreditTopupCheckoutInput,
) => ({
  credits: offer?.credits,
  price_usd: offer?.price_usd,
  auto_reload: input.auto_reload,
  purchase_mode: input.auto_reload ? 'auto_reload' : 'one_time',
  entrypoint: input.entrypoint,
  has_balance: (offer?.topup_credits ?? 0) > 0,
});

export const useCreditTopupCheckout = (offer: CreditTopupOffer | undefined) => {
  const { t } = useTranslation('subscription_page');
  const { capture } = usePostHogAnalytics();

  return useMutation<CreditTopupCheckoutUrl, unknown, CreditTopupCheckoutInput>({
    mutationKey: [QueryKey.subscription.creditTopupCheckout],
    onSuccess: ({ url }, input) => {
      const properties = checkoutEventProperties(offer, input);
      saveCreditTopupCheckoutAttribution({
        flow: 'purchase',
        entrypoint: input.entrypoint,
        auto_reload: input.auto_reload,
        has_balance: properties.has_balance,
      });
      capture(PostHogEventEnum.CreditTopupCheckoutStarted, properties);
      window.location.href = url;
    },
    onError: (_error, input) => {
      capture(
        PostHogEventEnum.CreditTopupCheckoutCreationFailed,
        checkoutEventProperties(offer, input),
      );
      toast.error(t('topup_panel.checkout_error'));
    },
  });
};
